"use client";

import Dexie, { type Table } from "dexie";

import type { SyncScopeWire } from "@/lib/sync/api-types";

import type { DeadLetterOp, LocalEntityRow, OutboxOp, SyncMetaRow } from "./types";

/**
 * Local-first Dexie database mirroring the server's offline-writable entities,
 * plus a small sync infrastructure (outbox + dead-letter + meta).
 *
 * IMPORTANT: this module must only be imported in the browser. The Dexie
 * constructor opens an IndexedDB connection on instantiation which requires
 * `window` / `indexedDB`. Server components and route handlers must not
 * import this module — use the server-side Prisma client instead.
 */

export class AcademyDB extends Dexie {
  // Offline-writable mirrors
  students!: Table<LocalEntityRow, string>;
  teachers!: Table<LocalEntityRow, string>;
  employees!: Table<LocalEntityRow, string>;
  groups!: Table<LocalEntityRow, string>;
  groupStudents!: Table<LocalEntityRow, string>;
  studentAttendances!: Table<LocalEntityRow, string>;
  groupAttendances!: Table<LocalEntityRow, string>;
  teacherCheckRecords!: Table<LocalEntityRow, string>;
  employeeCheckRecords!: Table<LocalEntityRow, string>;
  rentTeacherCheckRecords!: Table<LocalEntityRow, string>;
  teacherAbsences!: Table<LocalEntityRow, string>;
  employeeAbsences!: Table<LocalEntityRow, string>;
  classroomSchedules!: Table<LocalEntityRow, string>;
  rentTeachers!: Table<LocalEntityRow, string>;
  rentGroups!: Table<LocalEntityRow, string>;
  rentSessions!: Table<LocalEntityRow, string>;
  teacherPrograms!: Table<LocalEntityRow, string>;
  observations!: Table<LocalEntityRow, string>;
  programTrackings!: Table<LocalEntityRow, string>;
  enrollments!: Table<LocalEntityRow, string>;
  academicYears!: Table<LocalEntityRow, string>;
  terms!: Table<LocalEntityRow, string>;

  // Sync infrastructure
  outbox!: Table<OutboxOp, string>;
  deadletter!: Table<DeadLetterOp, string>;
  syncMeta!: Table<SyncMetaRow, string>;

  constructor(name = LEGACY_DB_NAME) {
    super(name);
    const versionOneStores = {
      students: "id, adminId, updatedAt, deletedAt, _syncStatus, [adminId+deletedAt]",
      teachers: "id, adminId, updatedAt, deletedAt, _syncStatus, [adminId+deletedAt], email",
      employees: "id, adminId, updatedAt, deletedAt, _syncStatus, [adminId+deletedAt], email",
      groups: "id, adminId, teacherId, updatedAt, deletedAt, _syncStatus, [adminId+deletedAt]",
      groupStudents: "id, groupId, studentId, updatedAt, deletedAt, [groupId+studentId]",
      studentAttendances: "id, studentId, date, updatedAt, deletedAt, [studentId+date]",
      groupAttendances: "id, groupId, studentId, monthIndex, sessionNumber, updatedAt, deletedAt",
      teacherCheckRecords: "id, teacherId, timestamp, updatedAt, deletedAt",
      employeeCheckRecords: "id, employeeId, timestamp, updatedAt, deletedAt",
      rentTeacherCheckRecords: "id, rentTeacherId, timestamp, updatedAt, deletedAt",
      teacherAbsences: "id, teacherId, createdAt, updatedAt, deletedAt",
      employeeAbsences: "id, employeeId, createdAt, updatedAt, deletedAt",
      classroomSchedules: "id, adminId, classroomNumber, dayOfWeek, updatedAt, deletedAt",
      rentTeachers: "id, adminId, updatedAt, deletedAt",
      rentGroups: "id, rentTeacherId, updatedAt, deletedAt",
      rentSessions: "id, rentGroupId, date, updatedAt, deletedAt",
      teacherPrograms: "id, adminId, updatedAt, deletedAt",
      observations: "id, studentId, updatedAt, deletedAt",
      programTrackings: "id, studentId, programId, updatedAt, deletedAt",
      enrollments: "id, studentId, programId, updatedAt, deletedAt",
      outbox: "opId, &[entity+id+sequence], status, createdAt",
      deadletter: "opId, entity, id, failedAt",
      syncMeta: "key",
    };
    this.version(1).stores(versionOneStores);
    this.version(2).stores({
      ...versionOneStores,
      academicYears: "id, adminId, startDate, endDate",
      terms: "id, academicYearId, orderIndex",
    });
  }
}

const LEGACY_DB_NAME = "academy-local";
const SCOPED_DB_PREFIX = `${LEGACY_DB_NAME}:`;
const ACTIVE_SCOPE_STORAGE_KEY = "academy-sync-active-scope";

let _db: AcademyDB | null = null;
let _dbName: string | null = null;

export function getDb(scopeHash?: string): AcademyDB {
  if (typeof window === "undefined") {
    throw new Error(
      "Local Dexie DB accessed on the server. Local-DB modules are client-only — use Prisma server-side.",
    );
  }
  const resolvedScopeHash = scopeHash ?? getActiveSyncScope()?.scopeHash;
  if (!resolvedScopeHash) {
    throw new Error("Local Dexie DB accessed without an active sync scope.");
  }

  const name = dbNameForScope(resolvedScopeHash);
  if (!_db || _dbName !== name) {
    _db?.close();
    _db = new AcademyDB(name);
    _dbName = name;
  }
  return _db;
}

export function dbNameForScope(scopeHash: string) {
  return `${SCOPED_DB_PREFIX}${scopeHash}`;
}

export function getActiveSyncScope(): SyncScopeWire | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(ACTIVE_SCOPE_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as SyncScopeWire;
    if (parsed?.scopeHash && parsed?.tenantId && parsed?.userId) return parsed;
  } catch {
    window.localStorage.removeItem(ACTIVE_SCOPE_STORAGE_KEY);
  }
  return null;
}

export async function activateSyncScope(scope: SyncScopeWire): Promise<AcademyDB> {
  return commitActiveSyncScope(scope);
}

export async function commitActiveSyncScope(scope: SyncScopeWire): Promise<AcademyDB> {
  if (typeof window === "undefined") {
    throw new Error("Cannot activate sync scope on the server.");
  }
  window.localStorage.setItem(ACTIVE_SCOPE_STORAGE_KEY, JSON.stringify(scope));
  const db = getDb(scope.scopeHash);
  await clearInactiveLocalDbs(scope.scopeHash);
  return db;
}

export async function clearInactiveLocalDbs(activeScopeHash?: string): Promise<void> {
  if (typeof window === "undefined") return;
  const activeName = activeScopeHash ? dbNameForScope(activeScopeHash) : null;
  const names = await listAcademyDbNames();
  await Promise.allSettled(names.filter((name) => name !== activeName).map((name) => Dexie.delete(name)));
}

export async function clearAllLocalDbs(): Promise<void> {
  if (typeof window === "undefined") return;
  _db?.close();
  _db = null;
  _dbName = null;
  window.localStorage.removeItem(ACTIVE_SCOPE_STORAGE_KEY);
  const names = await listAcademyDbNames();
  await Promise.allSettled(names.map((name) => Dexie.delete(name)));
}

async function listAcademyDbNames(): Promise<string[]> {
  const names = new Set<string>([LEGACY_DB_NAME]);
  const indexedDbWithDatabases = indexedDB as IDBFactory & {
    databases?: () => Promise<Array<{ name?: string }>>;
  };
  if (typeof indexedDbWithDatabases.databases === "function") {
    const dbs = await indexedDbWithDatabases.databases();
    for (const db of dbs) {
      if (db.name === LEGACY_DB_NAME || db.name?.startsWith(SCOPED_DB_PREFIX)) {
        names.add(db.name);
      }
    }
  }
  return [...names];
}

/**
 * Map between server entity names (camelCase) and local table names (plural).
 * The mapping is intentionally explicit so the sync engine doesn't need to
 * guess pluralisation.
 */
export const ENTITY_TO_TABLE: Record<string, keyof AcademyDB> = {
  student: "students",
  teacher: "teachers",
  employee: "employees",
  group: "groups",
  groupStudent: "groupStudents",
  studentAttendance: "studentAttendances",
  groupAttendance: "groupAttendances",
  teacherCheckRecord: "teacherCheckRecords",
  employeeCheckRecord: "employeeCheckRecords",
  rentTeacherCheckRecord: "rentTeacherCheckRecords",
  teacherAbsence: "teacherAbsences",
  employeeAbsence: "employeeAbsences",
  classroomSchedule: "classroomSchedules",
  rentTeacher: "rentTeachers",
  rentGroup: "rentGroups",
  rentSession: "rentSessions",
  teacherProgram: "teacherPrograms",
  observation: "observations",
  programTracking: "programTrackings",
  enrollment: "enrollments",
  academicYear: "academicYears",
  term: "terms",
};

export function tableForEntity(db: AcademyDB, entity: string): Table<LocalEntityRow, string> | null {
  const tableName = ENTITY_TO_TABLE[entity];
  if (!tableName) return null;
  return db.table(tableName as string) as Table<LocalEntityRow, string>;
}

export async function clearLocalDb(): Promise<void> {
  await clearAllLocalDbs();
}
